import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Link as DSLink,
  Loader,
  StackingAlertBanner,
} from '@saviynt/design-system';

import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { alertManagerActions } from '../../../shared/store/alertManagerSlice';
import formatReportType from '../../../shared/utils/reportType';
import { downloadBlobAsFile } from '../../../shared/utils/utils';
import { useLazyDownloadExportQuery } from '../data-layer/violationsReportApi';
import { selectVisibleCompletedItems } from '../data-store/violationsReportSelectors';

const downloadAcknowledgedIds = new Set<string>();

interface ExportBannerProps {
  className?: string;
}

interface DownloadLinkProps {
  exportId: string;
  isDownloading: boolean;
  downloadText: string;
  onDownload: (id: string) => Promise<void>;
}

const DownloadLink = memo(function DownloadLink({
  exportId,
  isDownloading,
  downloadText,
  onDownload,
}: DownloadLinkProps): ReactElement {
  if (isDownloading) {
    return (
      <Box className='Sod-DownloadLink-loading'>
        <Loader kind='loop' size='small' format='inline' color='inverse' />
      </Box>
    );
  }

  return (
    <DSLink
      text={downloadText}
      kind='button'
      onClick={() => onDownload(exportId)}
      color='neutral'
      suffixIconKind='Download'
      dataTheme='dark'
    />
  );
});

function ExportBanner({ className }: ExportBannerProps): ReactElement | null {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const [downloadExport] = useLazyDownloadExportQuery();

  // Minimized selections
  // Keep selector in case future logic needs lookup, but not required for rendering
  // const exportIdsByReportType = useTypedSelector(selectExportIdsByReportType);
  const visibleCompletedItems = useTypedSelector(selectVisibleCompletedItems);
  const shownSuccessExportIds = useTypedSelector(
    (state) => state.aag.alertManager.shownSuccessExportIds
  );

  // Local state
  // Rely on global shownSuccessExportIds to gate once-only banners; no local dismissed set
  const [currentDownloadingExportId, setCurrentDownloadingExportId] = useState<
    string | null
  >(null);

  // Info/Error banners are now handled by BannerManager; only render success banners here

  // Track scheduled auto-dismiss marks to avoid duplicate renders during polling
  const scheduledMarkShownRef = useRef<Set<string>>(new Set());

  // Success banners with download link (stacked by report type)
  const itemsToRender = useMemo(
    () =>
      visibleCompletedItems.filter(({ exportId }) => {
        if (typeof exportId !== 'string') {
          return false; // only render when exportId is available
        }

        if (scheduledMarkShownRef.current.has(exportId)) {
          return false;
        }

        if (
          shownSuccessExportIds[exportId] ||
          downloadAcknowledgedIds.has(exportId)
        ) {
          return false;
        }

        return true;
      }),
    [visibleCompletedItems, shownSuccessExportIds]
  );

  // Failsafe: mark success as shown after autoDismiss delay even if onCancel isn't fired
  useEffect(() => {
    itemsToRender.forEach(({ exportId }) => {
      if (typeof exportId !== 'string') {
        return;
      }

      if (!scheduledMarkShownRef.current.has(exportId)) {
        scheduledMarkShownRef.current.add(exportId);
        window.setTimeout(() => {
          if (!shownSuccessExportIds[exportId]) {
            dispatch(alertManagerActions.markSuccessShown(exportId));
          }

          scheduledMarkShownRef.current.delete(exportId);
        }, 3200);
      }
    });
  }, [itemsToRender, shownSuccessExportIds, dispatch]);

  return (
    <>
      {itemsToRender.map(({ reportType, exportId }) => (
        <StackingAlertBanner
          className={className}
          key={`completed-${exportId ?? ''}`}
          colorTheme='Success'
          title={l('Export Completed')}
          description={`${formatReportType(
            reportType
          )} export has completed successfully.`}
          isVisible
          shouldAutoDismiss
          autoDismissDelay={3000}
          onCancel={() => {
            const id = exportId ?? '';

            if (currentDownloadingExportId === id) {
              setCurrentDownloadingExportId(null);
            }

            // Mark this completed export as shown to avoid re-rendering again
            if (!shownSuccessExportIds[id]) {
              dispatch(alertManagerActions.markSuccessShown(id));
            }
          }}
          LinkComponent={
            typeof exportId === 'string' ? (
              <DownloadLink
                key={`dl-${exportId}-${
                  currentDownloadingExportId === exportId ? 1 : 0
                }`}
                exportId={exportId}
                isDownloading={currentDownloadingExportId === exportId}
                downloadText={l('Download')}
                onDownload={async (id: string) => {
                  try {
                    setCurrentDownloadingExportId(id);

                    const file = await downloadExport(id).unwrap();

                    const suggestedFileName = (
                      file as unknown as { fileName?: string }
                    ).fileName;
                    const finalFileName = suggestedFileName ?? `${id}.xlsx`;

                    downloadBlobAsFile(file, finalFileName);

                    downloadAcknowledgedIds.add(id);
                  } catch (e) {
                    Logger.log('error', 'Download failed:', e);
                  } finally {
                    setCurrentDownloadingExportId(null);
                  }
                }}
              />
            ) : null
          }
        />
      ))}
    </>
  );
}

ExportBanner.defaultProps = {
  className: '',
};

export default memo(ExportBanner);
